import classNames from 'classnames';
import * as React from 'react';

import { CheckboxDecorator } from '../../../components/CheckboxDecorator/CheckboxDecorator';
import { HoverTooltipWrapper } from '../../../components/HoverTooltipWrapper/HoverTooltipWrapper';
import { Shape2, Shape2Icon, Shape2Theme } from '../../../components/Shape2/Shape2';
import { Bem } from '../../../service/Bem/Bem';
import { Omit } from '../../../typings/utils';
import { CreativeOption, CreativeOptionProps } from '../CreativeOption/CreativeOption';
import {
    CommonOptionGroupName,
    CommonOptionName,
    CreativeOptionData,
    CreativeOptionsGroupErrors,
    CreativeOptionType,
    CreativeOptionValue
} from '../typings';

import './CreativeOptionGroup.css';

export interface CreativeOptionGroupProps {
    cls?: string;
    title: string;
    tooltip?: string | React.ReactNode;

    canBeHidden?: boolean;
    visible?: boolean;
    onVisibleChange?: (groupName: string) => void;

    name: string;
    options: Array<CreativeOptionData>;
    onOptionChange: (optionGroupName: string, optionName: string, value: string) => void;
    values: Record<string, CreativeOptionValue>;
    errors?: CreativeOptionsGroupErrors;
    tooltipsVisible?: boolean;

    isDocCenterTooltip?: boolean;
}

const BLOCK = 'creative-option-group';

export class CreativeOptionGroup extends React.PureComponent<CreativeOptionGroupProps> {
    render(): React.ReactNode {
        const { cls, title, canBeHidden, name } = this.props;

        return (
            <div className={classNames(BLOCK, cls)} id={`#parameter-id-${name}`}>
                {title !== '' && (
                    <div
                        className={Bem.cls(BLOCK, 'title', {
                            'can-be-hidden': Boolean(canBeHidden)
                        })}
                        onClick={this.onVisibleChange}
                    >
                        {this.renderCheckbox()}
                        {title}
                        {this.renderTooltip()}
                    </div>
                )}
                {this.renderOptions()}
            </div>
        );
    }

    private renderCheckbox(): React.ReactNode {
        const { canBeHidden, visible } = this.props;

        if (!canBeHidden) {
            return null;
        }

        return (
            <CheckboxDecorator
                cls={Bem.cls(BLOCK, 'checkbox')}
                size="s"
                view="classic"
                theme="normal"
                checked={visible}
            />
        );
    }

    private renderTooltip(): React.ReactNode {
        const { tooltip, isDocCenterTooltip } = this.props;

        if (!tooltip) {
            return null;
        }

        return (
            <HoverTooltipWrapper to={'bottom'} tooltipText={tooltip} isDoccenterTooltip={isDocCenterTooltip}>
                <Shape2
                    icon={Shape2Icon.help}
                    theme={Shape2Theme.default}
                    width={12}
                    height={12}
                    cls={Bem.cls(BLOCK, 'icon-info')}
                />
            </HoverTooltipWrapper>
        );
    }

    private getCommonParams = (
        option: CreativeOptionData
    ): Omit<CreativeOptionProps, 'countryId'> & { key: string } => {
        const { values, errors, tooltipsVisible, name, isDocCenterTooltip } = this.props;

        return {
            key: option.name,
            data: option,
            errors: errors && errors[option.name],
            change: this.onOptionChange,
            cls: Bem.cls(BLOCK, 'option'),
            placeholder:
                option.type === CreativeOptionType.Text ? values[CommonOptionName.placeholder] || '' : undefined,
            tooltipsVisible,
            groupName: name,
            isDocCenterTooltip,
            value:
                option.type === CreativeOptionType.Disclaimer
                    ? values[CommonOptionName.disclaimer]
                    : values[option.name]
        };
    };

    private renderOptions(): React.ReactNode {
        const { canBeHidden, visible, options, values, name } = this.props;

        if (canBeHidden && !visible) {
            return null;
        }

        return options.map((option: CreativeOptionData) => {
            switch (name) {
                case CommonOptionGroupName.disclaimer:
                    return (
                        <CreativeOption
                            {...this.getCommonParams(option)}
                            countryId={values[CommonOptionName.disclaimerCountry]}
                        />
                    );

                default:
                    return <CreativeOption {...this.getCommonParams(option)} />;
            }
        });
    }

    private onOptionChange = (optionName: string, value: string): void => {
        const { name, onOptionChange } = this.props;

        onOptionChange(name, optionName, value);
    };

    private onVisibleChange = (): void => {
        const { name, canBeHidden, onVisibleChange } = this.props;

        if (!onVisibleChange || !canBeHidden) {
            return;
        }

        onVisibleChange(name);
    };
}
